import { useMemo } from 'react'
import {
  getNestedSubelementsByType,
  getEventLabel,
  getMostRecentFieldValue,
  getImage,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import uniqBy from 'lodash.uniqby'
import { useTranslation } from 'react-i18next'
import { containsRelationshipType } from '../utils/eventsParsing.utils'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets secondary text (date | place) for an event
 */
const getSecondaryText = (event) => {
  const dates = getNestedSubelementsByType(event, 'DATE', true)
  const places = getNestedSubelementsByType(event, 'PLACE', true)
  const dateFieldValue = dates.length > 0 ? getMostRecentFieldValue(dates[0]) : null
  const placeFieldValue = places.length > 0 ? getMostRecentFieldValue(places[0]) : null
  return [dateFieldValue, placeFieldValue].filter(Boolean).join(' | ')
}

/**
 * Creates filter function to validate events
 */
const createEventFilter = (person, alreadyAddedEventIds, isPrimarySection) => (ev) => {
  const attachedToThisPersonAlready = ev.superElements?.some((supEl) => supEl.id === person?.id)
  const isCoupleEvent = ev.typeData?.elementTypes?.includes('RELATIONSHIP')
  const alreadyAddedToForm = alreadyAddedEventIds.has(ev.id)
  const isPrimaryOnlyEvent = ev.typeData?.primaryOnly
  const passesPrimaryFilter = isPrimarySection ? true : !isPrimaryOnlyEvent
  const justPrimaryEventsOnPrimarySection = isPrimarySection ? ev.primary : true

  return (
    !attachedToThisPersonAlready &&
    !isCoupleEvent &&
    !alreadyAddedToForm &&
    passesPrimaryFilter &&
    justPrimaryEventsOnPrimarySection
  )
}

/**
 * Maps event to record suggestion format
 */
const mapToRecordSuggestion = (event, slsTypes, t) => ({
  key: `existing_${event.id}`,
  primaryText: getEventLabel(event, slsTypes),
  secondaryText: getSecondaryText(event),
  group: t('edit-events.existing-events-in-record', 'Existing Events in This Record'),
  existingEvent: event,
  isExistingEvent: true,
})

/**
 * Maps event to image suggestion format
 */
const mapToImageSuggestion = (event, slsTypes, t) => ({
  key: `existing_${event.id}`,
  primaryText: getEventLabel(event, slsTypes),
  secondaryText: getSecondaryText(event),
  group: t('edit-events.existing-events-in-image', 'Existing Events in This Image'),
  existingEvent: event,
  isExistingEvent: true,
})

/**
 * Maps event type to new event suggestion format
 */
const mapToNewEventSuggestion = (type, t) => ({
  key: type.eventType,
  primaryText: type.displayLabel,
  isRelationshipEvent: containsRelationshipType(type),
  relType: type?.relType || null,
  group: t('edit-events.new-event', 'New Event'),
  isExistingEvent: false,
})

/**
 * Collects IDs of events already added to the form
 */
const collectAlreadyAddedEventIds = (currentFormEvents) => {
  return new Set([
    ...(currentFormEvents?.primary || []).filter((e) => e.existingEventId).map((e) => e.existingEventId),
    ...(currentFormEvents?.additional || []).filter((e) => e.existingEventId).map((e) => e.existingEventId),
  ])
}

/**
 * Extracts events from record
 */
const extractRecordEvents = (currentRecord, filterFunc, slsTypes, t) => {
  if (!currentRecord) return []

  return getNestedSubelementsByType(currentRecord, 'EVENT')
    .filter(filterFunc)
    .map((event) => mapToRecordSuggestion(event, slsTypes, t))
}

/**
 * Extracts events from image (excluding record events)
 */
const extractImageEvents = (currentImage, filterFunc, recordEvents, slsTypes, t) => {
  if (!currentImage) return []

  return getNestedSubelementsByType(currentImage, 'EVENT')
    .filter(filterFunc)
    .filter((ev) => !recordEvents.some((recordEv) => recordEv.existingEvent.id === ev.id))
    .map((event) => mapToImageSuggestion(event, slsTypes, t))
}

/**
 * Extracts events from floating persons
 */
const extractFloatingEvents = (currentImage, filterFunc, slsTypes, t) => {
  if (!currentImage) return []

  const floatingPersons = getNestedSubelementsByType(currentImage.floatingElements || [], 'PERSON')

  return floatingPersons.flatMap((fp) =>
    getNestedSubelementsByType(fp, 'EVENT')
      .filter(filterFunc)
      .map((event) => mapToImageSuggestion(event, slsTypes, t))
  )
}

/**
 * Filters and maps new event types
 */
const getNewEventTypeSuggestions = (slsTypes, isPrimarySection, t) => {
  return slsTypes.events
    .filter((type) => (isPrimarySection ? true : !type.primaryOnly))
    .map((type) => mapToNewEventSuggestion(type, t))
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Custom hook to generate event suggestions for the event type autosuggest.
 *
 * This hook provides a filtered and grouped list of event options including:
 * - Existing events from the record (that can be linked)
 * - Existing events from the image (that can be linked)
 * - New event types (to create new events)
 *
 * @param {Object} params
 * @param {Object} params.person - The person object associated with the events
 * @param {Object} params.slsTypes - SLS types object containing event definitions
 * @param {Object} params.currentFormEvents - Current form state with primary/additional events
 * @param {boolean} params.isPrimarySection - Whether this is the primary event section
 * @param {Object} params.images - SLS images data
 * @param {string} params.currentImageArk - ARK of the current image
 * @returns {Array} Array of suggestion objects with key, primaryText, group, etc.
 */
export const useEventSuggestions = ({
  person,
  slsTypes,
  currentFormEvents,
  isPrimarySection,
  images,
  currentImageArk,
}) => {
  const [t] = useTranslation()

  return useMemo(() => {
    // If no person prop, return only standard event types (filtered by primaryOnly)
    if (!person) {
      return getNewEventTypeSuggestions(slsTypes, isPrimarySection, t)
    }

    // Collect IDs of events already added to the form to prevent duplicates
    const alreadyAddedEventIds = collectAlreadyAddedEventIds(currentFormEvents)

    // Create filter function with captured variables
    const filterFunc = createEventFilter(person, alreadyAddedEventIds, isPrimarySection)

    // Get current record and image
    const currentRecord = person?.recordId ? getNestedOrFloatingElementById(images, person.recordId) : null
    const currentImage = getImage(images, currentImageArk)

    // Extract events from different sources
    const eventsInThisRecord = extractRecordEvents(currentRecord, filterFunc, slsTypes, t)
    const eventsInThisImage = extractImageEvents(currentImage, filterFunc, eventsInThisRecord, slsTypes, t)
    const eventsFromFloating = extractFloatingEvents(currentImage, filterFunc, slsTypes, t)

    // Combine existing events and deduplicate
    const existingEvents = uniqBy([...eventsInThisRecord, ...eventsInThisImage, ...eventsFromFloating], (ev) => ev.key)

    // Get new event type suggestions
    const newEventTypes = getNewEventTypeSuggestions(slsTypes, isPrimarySection, t)

    // Combine existing events first, then new event types
    return [...existingEvents, ...newEventTypes]
  }, [slsTypes, images, currentImageArk, person, t, currentFormEvents, isPrimarySection])
}
